import React, { useEffect, useMemo } from "react"
import RunBlueprintDialog from "../components/dialogs/run-blueprint"
import { useMediaQuery } from "@mui/material"
import DashboardStore, { useDispatch, useSelector, Actions, useRegisterTrigger, Triggers, Dialogs } from "../stores/dashboard.store"
import ProjectsNav from "../components/nav/projects-nav"
import useFetch from "../hooks/useFetch"
import { fetchBlueprints, getBlueprint, getMe, getCurrentPlan, configColors, getMeImportant, getTheme } from "../api-client/data"
import { DashboardNavigationItems, NavigationMap } from '../config/nav'
import { useHideUnimportantErrors } from '../hooks/useFixErrors'
import LinearProgress from '@mui/material/LinearProgress'
import UnsavedWork from "../components/dialogs/unsaved-work"
import Tutorial from "../components/dialogs/tutorial"
import Feedback from "../components/dialogs/feedback"

import "../styles/xyflow.css"
import "../styles/style.css"
import NodeInfo from "../components/dialogs/node-info"
import Snackbar from "../components/snackbar/snackbar"
import themes from "../config/themes"

const DashboardBody = () => {
    const dispatch = useDispatch()
    const data = useSelector()

    const accountPlanFetch = useFetch(getCurrentPlan)
    const user = useFetch(getMe)
    const projectListFetch = useFetch(fetchBlueprints)

    const userImportantMessages = useFetch(getMeImportant)

    useRegisterTrigger(Triggers.RefreshUserInfo, user.refresh)
    useRegisterTrigger(Triggers.RefreshProjectList, projectListFetch.refresh)

    const colors = useFetch(configColors)
    const isMobile = useMediaQuery("(max-width: 900px)")

    const updateTheme = async () => {
        // TODO: use the setting instead of the "theme" endpoint
        const theme = await getTheme()
        const html = document.documentElement
        
        themes.forEach((t) => {
            if (t.className && html.classList.contains(t.className)) {
                html.classList.remove(t.className)
            }
        })
        
        if (theme?.theme !== "") {
            html.classList.add(theme?.theme)
        }
        dispatch(Actions.SetTheme, theme?.theme)
    }

    useRegisterTrigger(Triggers.RefreshTheme, updateTheme)

    useEffect(() => {
        updateTheme()
    }, [])

    const selectProject = async ({ id, name }, navigate = true) => {
        dispatch(Actions.SetIndeterminate, { visible: true, isFullApp: !data?.activeProjectRequiresSave })

        try {
            const project = await getBlueprint(id)

            dispatch(Actions.SetIndeterminate, false)

            if (navigate === true) {
                dispatch(Actions.SetActiveProjectAndNavigate, {
                    project: {
                        ...project.data,
                        version: project?.version,
                        name: name || data?.activeProject?.name,
                        id: project?.id,
                        defaultValues: project?.defaultValues || {}
                    },
                    nav: DashboardNavigationItems._id,
                    view: isMobile ? DashboardNavigationItems.Request : DashboardNavigationItems.Blueprint
                })
                return
            }

            dispatch(Actions.SetActiveProject, {
                ...project.data,
                version: project?.version,
                name: name || data?.activeProject?.name,
                id: project?.id,
                defaultValues: project?.defaultValues || {}
            })

        } catch { }
    }

    useEffect(() => {
        if (userImportantMessages?.data?.requireTutorial === true) {
            dispatch(Actions.SetShowTutorial, true)
        }
    }, [userImportantMessages?.data])

    useRegisterTrigger(Triggers.SetActiveProject, selectProject)

    useEffect(() => {
        if (data?.activeProject?.id !== undefined) {
            selectProject(data?.activeProject, false)
        }
    }, [data?.activeProject?.id])

    useEffect(() => {
        dispatch(Actions.SetUser, user?.data)
    }, [user?.data])

    useEffect(() => {
        dispatch(Actions.SetIndeterminate, { visible: accountPlanFetch.loading, value: undefined, isFullApp: accountPlanFetch.loading })
        dispatch(Actions.SetAccountPlanConstraints, accountPlanFetch?.data?.plan?.constraints)
    }, [accountPlanFetch?.data, accountPlanFetch?.loading])

    useRegisterTrigger(Triggers.RefreshAccountPlan, accountPlanFetch.refresh)

    useEffect(() => {
        dispatch(Actions.SetColors, colors?.data)
    }, [colors?.data])

    useEffect(() => {
        dispatch(Actions.SetProjectList, projectListFetch.data)
    }, [projectListFetch?.data])

    const navItems = useMemo(() => NavigationMap[data?.dashboardNavigation], [data?.dashboardNavigation])

    const closeTutorial = () => dispatch(Actions.SetShowTutorial, false)
    const closeFeedback = () => dispatch(Actions.SetShowFeedbackForm, false)
    const closeNodeInfo = () => dispatch(Actions.SetApplicationDialog, { dialog: Dialogs.NodeInformation, visible: false })

    return (
        <main>
            {
                data?.loadingIndeterminate?.visible && (
                    <div style={{ position: 'absolute', top: 0, right: 0, width: '100%' }}>
                        <LinearProgress variant={data?.loadingIndeterminate?.value !== undefined && "determinate"} value={data?.loadingIndeterminate?.value} />
                    </div>
                )
            }
            <NodeInfo show={data?.dialogs?.nodeInformation?.visible} onClose={closeNodeInfo} data={data?.dialogs?.nodeInformation?.data} />
            <UnsavedWork show={data?.unsavedForm?.show} />
            <Feedback show={data?.showFeedbackForm} onClose={closeFeedback} />
            <Tutorial show={data?.showTutorial} onClose={closeTutorial} />
            <ProjectsNav
                setActiveProject={selectProject}
                setActivePage={(page) => dispatch(Actions.SetDashboardView, page)}
                navItems={navItems}                
            />
        </main>
    )
}

const DashboardPage = () => {
    useHideUnimportantErrors()

    return (
        <Snackbar>
            <DashboardStore>
                <RunBlueprintDialog />
                <DashboardBody />
            </DashboardStore>
        </Snackbar>
    )
}

export default DashboardPage
